import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/data/products";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

// Which categories to show in the "What are you looking for?" slider.
// Rings, Bracelets, Gifts & Packaging intentionally removed per brief.
const ALLOWED_CATEGORY_IDS = ["earrings", "necklaces", "cuffs", "sets", "ocean"];

const ShopByCategory = () => {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [activeDot, setActiveDot] = useState(0);

  const cats = CATEGORIES.filter((c) => ALLOWED_CATEGORY_IDS.includes(c.id));

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    // Compute active dot based on scroll position
    const card = el.querySelector('[data-slide-card="true"]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth;
    setActiveDot(Math.round(el.scrollLeft / step));
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    // Card width + gap ≈ first child offsetWidth + 24px gap
    const card = el.querySelector('[data-slide-card="true"]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.85;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const scrollToDot = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-slide-card="true"]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.85;
    el.scrollTo({ left: idx * step, behavior: "smooth" });
  };

  return (
    <section
      id="categories"
      className="py-16 lg:py-24"
      style={{ background: "#FDF6F0" }}
      data-testid="shop-by-category"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-10 lg:mb-14">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="gold-divider" />
              <span className="overline">Browse</span>
            </div>
            <h2
              className="font-serif text-plum leading-[1] tracking-tight"
              style={{ fontSize: "clamp(32px, 4.4vw, 56px)" }}
            >
              What are you <span className="italic text-blush">looking</span> for?
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Slider arrows — visible on all sizes, disabled at edges */}
            <div className="hidden sm:flex items-center gap-2" aria-hidden>
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                disabled={!canPrev}
                data-testid="shop-by-cat-prev"
                aria-label="Previous categories"
                className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-plum transition-all duration-300 hover:bg-plum hover:text-cream hover:border-plum disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                disabled={!canNext}
                data-testid="shop-by-cat-next"
                aria-label="Next categories"
                className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-plum transition-all duration-300 hover:bg-plum hover:text-cream hover:border-plum disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <Link
              to="/shop"
              className="text-sm tracking-[0.18em] uppercase text-gold hover:text-blush transition-colors flex items-center gap-1.5"
              data-testid="view-all-categories"
            >
              View all <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>

        {/* Swipeable slider — smooth snap on both mobile and desktop */}
        <div className="relative -mx-5 lg:mx-0">
          <div
            ref={trackRef}
            className="flex gap-5 lg:gap-6 overflow-x-auto no-scrollbar px-5 lg:px-0 pb-4 lg:pb-2 scroll-smooth"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
            data-testid="shop-by-cat-track"
          >
            {cats.map((cat) => (
              <Link
                key={cat.id}
                to={`/shop?category=${cat.id}`}
                data-slide-card="true"
                data-testid={`category-${cat.id}`}
                className="group flex-shrink-0 w-[70vw] sm:w-[300px] lg:w-[300px] xl:w-[320px]"
                style={{ scrollSnapAlign: "start" }}
              >
                <div className="relative aspect-square rounded-[20px] overflow-hidden bg-pearl mb-4">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-plum/30 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity" />
                </div>
                <div>
                  <h3 className="font-serif text-plum text-xl lg:text-[22px] leading-tight mb-1 group-hover:italic transition-all">
                    {cat.name}
                  </h3>
                  <p className="text-[12px] text-plum/55 tracking-wide">{cat.sub}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Soft edge fades (desktop only, helps signal there's more to swipe) */}
          <div
            aria-hidden
            className="pointer-events-none hidden lg:block absolute top-0 bottom-6 left-0 w-16 transition-opacity duration-300"
            style={{
              background:
                "linear-gradient(to right, #FDF6F0 0%, rgba(253,246,240,0) 100%)",
              opacity: canPrev ? 1 : 0,
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none hidden lg:block absolute top-0 bottom-6 right-0 w-16 transition-opacity duration-300"
            style={{
              background:
                "linear-gradient(to left, #FDF6F0 0%, rgba(253,246,240,0) 100%)",
              opacity: canNext ? 1 : 0,
            }}
          />
        </div>

        {/* Slider dots pagination */}
        <div
          className="flex items-center justify-center gap-2 mt-6"
          data-testid="shop-by-cat-dots"
          role="tablist"
          aria-label="Category slider pages"
        >
          {cats.map((_, i) => {
            const isActive = i === activeDot;
            return (
              <button
                key={i}
                type="button"
                onClick={() => scrollToDot(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={isActive}
                data-testid={`shop-by-cat-dot-${i}`}
                className="group py-2 focus:outline-none"
              >
                <span
                  className="block rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: isActive ? "26px" : "8px",
                    height: "6px",
                    background: isActive
                      ? "linear-gradient(90deg,#C5985A,#D4537E)"
                      : "rgba(74,44,53,0.18)",
                    boxShadow: isActive
                      ? "0 2px 6px rgba(197,152,90,0.35)"
                      : "none",
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ShopByCategory;
